import React, { useState, useEffect } from 'react';
import { useOrgAuth } from '../../../contexts/OrgAuthContext';
import {
  getSchemes, createScheme, updateScheme, archiveScheme,
  getSchemeReferrals, claimSchemeReferral, updateReferralStatus, releaseReferralClaim, daysSince,
  addGovApplicationMessage
} from '../../../api/programmesApi';
import { getMembers } from '../../../api/communityApi';
import { exportRowsToExcel } from '../../../utils/exportExcel';

const TABS = [
  { key: 'library', label: 'Government Programme Library' },
  { key: 'referrals', label: 'Applications Dashboard' },
];

const STATUS_BADGE = {
  referred: { cls: 'bb-badge bb-badge--gray', label: 'Referred' },
  in_progress: { cls: 'bb-badge', style: { background: '#FEF9C3', color: '#92400E' }, label: 'In Progress' },
  approved: { cls: 'bb-badge bb-badge--green', label: 'Approved' },
  rejected: { cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' }, label: 'Rejected' },
};

const EMPTY_SCHEME = {
  name: '', description: '', administrator: '',
  eligibility: { widowStatus: [], maxIncome: '', childrenCount: '', ageGroups: [], monthlyHouseholdIncome: '', childAge: '' },
  benefits: '', documentsRequired: '', deadline: '', applicationUrl: ''
};

const WIDOW_STATUS_OPTIONS = ['Widowed', 'Divorced', 'Single Parent'];
const AGE_GROUP_OPTIONS = ['Under 18', '18–35', '36–55', '55+'];

function SlaCell({ days }) {
  if (days >= 5) return <span style={{ color: '#DC2626', fontWeight: 600 }}>⚠️ {days}d</span>;
  return <span>{days}d</span>;
}

export default function GovernmentSchemes({ orgId }) {
  const { currentUser } = useOrgAuth();
  const [activeTab, setActiveTab] = useState('library');

  // Scheme Library state
  const [schemes, setSchemes] = useState([]);
  const [schemesLoading, setSchemesLoading] = useState(false);
  const [schemesError, setSchemesError] = useState('');
  const [showAddScheme, setShowAddScheme] = useState(false);
  const [schemeForm, setSchemeForm] = useState(EMPTY_SCHEME);
  const [schemeSubmitting, setSchemeSubmitting] = useState(false);
  const [editingScheme, setEditingScheme] = useState(null);

  // Find Matches modal
  const [findMatchesScheme, setFindMatchesScheme] = useState(null);
  const [matchResults, setMatchResults] = useState(null); // null = not run yet
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState('');

  // Referral state
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [referralsError, setReferralsError] = useState('');
  const [referralFilter, setReferralFilter] = useState('all');
  const [referralSchemeFilter, setReferralSchemeFilter] = useState('all');
  const [statusUpdating, setStatusUpdating] = useState({});

  // Status update modal
  const [statusModal, setStatusModal] = useState(null); // { referralId, newStatus }
  const [statusNote, setStatusNote] = useState('');

  // Reporting state
  const [stats, setStats] = useState({ activeSchemes: 0, openReferrals: 0, approvalsThisMonth: 0, avgDays: 0 });

  // Referral view modal
  const [viewApp, setViewApp] = useState(null);
  const [appMessage, setAppMessage] = useState('');
  const [appMessageSending, setAppMessageSending] = useState(false);
  const [appsError, setAppsError] = useState('');

  useEffect(() => {
    if (activeTab === 'library') { loadSchemes(); loadReferrals(); }
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'referrals') loadReferrals();
  }, [activeTab, orgId]);

  useEffect(() => {
    if (activeTab === 'reporting') computeStats();
  }, [activeTab, schemes, referrals]);


  const loadSchemes = async () => {
    setSchemesLoading(true);
    setSchemesError('');
    try {
      const data = await getSchemes(orgId);
      setSchemes(data || []);
    } catch (e) {
      setSchemesError(e.message || 'Failed to load schemes.');
    } finally {
      setSchemesLoading(false);
    }
  };

  const loadReferrals = async () => {
    setReferralsLoading(true);
    setReferralsError('');
    try {
      const data = await getSchemeReferrals(orgId);
      setReferrals(data || []);
    } catch (e) {
      setReferralsError(e.message || 'Failed to load referrals.');
    } finally {
      setReferralsLoading(false);
    }
  };

  const computeStats = () => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const activeSchemes = schemes.filter(s => s.status !== 'archived').length;
    const openReferrals = referrals.filter(r => r.status !== 'approved' && r.status !== 'rejected').length;
    const approvalsThisMonth = referrals.filter(r => {
      if (r.status !== 'approved') return false;
      const d = new Date(r.resolvedAt?.toDate?.() || r.resolvedAt);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    }).length;
    const resolved = referrals.filter(r => r.status === 'approved' || r.status === 'rejected');
    const avgDays = resolved.length
      ? Math.round(resolved.reduce((sum, r) => sum + (daysSince(r.createdAt) || 0), 0) / resolved.length)
      : 0;
    setStats({ activeSchemes, openReferrals, approvalsThisMonth, avgDays });
  };

  const handleEditScheme = (scheme) => {
    const docs = scheme.documentsRequired;
    setSchemeForm({
      name:              scheme.name || '',
      description:       scheme.description || '',
      administrator:     scheme.administrator || '',
      eligibility: {
        widowStatus:             scheme.eligibility?.widowStatus || [],
        maxIncome:               scheme.eligibility?.maxIncome || '',
        childrenCount:           scheme.eligibility?.childrenCount || '',
        ageGroups:               scheme.eligibility?.ageGroups || [],
        monthlyHouseholdIncome:  scheme.eligibility?.monthlyHouseholdIncome || '',
        childAge:                scheme.eligibility?.childAge || '',
      },
      benefits:          scheme.benefits || '',
      documentsRequired: Array.isArray(docs) ? docs.join(', ') : (docs || ''),
      deadline:          scheme.deadline || '',
      applicationUrl:    scheme.applicationUrl || '',
    });
    setEditingScheme(scheme);
    setShowAddScheme(true);
  };

  const handleSchemeSubmit = async (e) => {
    e.preventDefault();
    setSchemeSubmitting(true);
    setSchemesError('');
    try {
      const docs = schemeForm.documentsRequired;
      const payload = {
        ...schemeForm,
        documentsRequired: Array.isArray(docs) ? docs : docs.split(',').map(s => s.trim()).filter(Boolean),
        orgId,
      };
      if (editingScheme) {
        await updateScheme(orgId, editingScheme.id, payload);
      } else {
        await createScheme(orgId, { ...payload, createdBy: currentUser.uid });
      }
      setShowAddScheme(false);
      setSchemeForm(EMPTY_SCHEME);
      setEditingScheme(null);
      await loadSchemes();
    } catch (e) {
      setSchemesError(e.message || 'Failed to save scheme.');
    } finally {
      setSchemeSubmitting(false);
    }
  };

  const handleArchive = async (schemeId) => {
    if (!window.confirm('Archive this scheme?')) return;
    try {
      await archiveScheme(orgId, schemeId);
      await loadSchemes();
    } catch (e) {
      setSchemesError(e.message || 'Failed to archive scheme.');
    }
  };

  const adminName = () => currentUser.name || currentUser.displayName || currentUser.email || 'Admin';

  const handleClaim = async (referralId) => {
    setStatusUpdating(p => ({ ...p, [referralId]: true }));
    try {
      await claimSchemeReferral(orgId, referralId, currentUser.uid, adminName(), '');
      await loadReferrals();
    } catch (e) {
      setReferralsError(e.message || 'Failed to claim referral.');
    } finally {
      setStatusUpdating(p => ({ ...p, [referralId]: false }));
    }
  };

  const handleRelease = async (referralId) => {
    try {
      await releaseReferralClaim(orgId, referralId);
      await loadReferrals();
    } catch (e) {
      setReferralsError(e.message || 'Failed to release referral.');
    }
  };

  const handleViewApp = (app) => {
    setViewApp(app);
    setAppsError('');
    setAppMessage('');
  };

  const handleSendAppMessage = async () => {
    if (!appMessage.trim()) return;
    setAppMessageSending(true);
    setAppsError('');
    try {
      const name = adminName();
      await addGovApplicationMessage(orgId, viewApp.id, currentUser.uid, name, 'admin', appMessage.trim());
      const msg = { senderId: currentUser.uid, senderName: name, senderRole: 'admin', text: appMessage.trim(), sentAt: new Date().toISOString() };
      setViewApp(prev => ({ ...prev, correspondence: [...(prev.correspondence || []), msg] }));
      setAppMessage('');
    } catch (e) {
      setAppsError(e.message || 'Failed to send message.');
    } finally {
      setAppMessageSending(false);
    }
  };

  const handleStatusChange = async () => {
    const { referralId, newStatus } = statusModal;
    setStatusUpdating(p => ({ ...p, [referralId]: true }));
    try {
      await updateReferralStatus(orgId, referralId, newStatus, statusNote, currentUser.uid, adminName());
      setStatusModal(null);
      setStatusNote('');
      await loadReferrals();
    } catch (e) {
      setReferralsError(e.message || 'Failed to update status.');
    } finally {
      setStatusUpdating(p => ({ ...p, [referralId]: false }));
    }
  };

  const calcAge = (dob) => {
    if (!dob) return null;
    const birth = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const memberMatchesScheme = (member, scheme) => {
    const elig = scheme.eligibility || {};
    const verified = [];     // criteria confirmed from member data
    const unverifiable = []; // criteria that exist but can't be checked from profile

    // AND condition 1 — widow / marital status
    if (elig.widowStatus?.length) {
      const memberIsWidowed = member.widowStage === 'current' || member.widowStage === 'past';
      const requiresWidowed = elig.widowStatus.some(s => s.toLowerCase() === 'widowed');
      if (requiresWidowed) {
        if (!memberIsWidowed) return null; // fails AND — exclude
        verified.push('Widowed');
      } else {
        // 'Divorced' / 'Single Parent' are not stored in member profiles — flag for manual check
        unverifiable.push(`Status: ${elig.widowStatus.join(' / ')} (verify manually)`);
      }
    }

    // AND condition 2 — age group
    if (elig.ageGroups?.length) {
      const age = calcAge(member.dateOfBirth);
      if (age === null) {
        // DOB missing — can't verify, flag it
        unverifiable.push('Age group (DOB not set — verify manually)');
      } else {
        const inGroup = elig.ageGroups.some(g => {
          if (g === 'Under 18') return age < 18;
          if (g === '18–35')    return age >= 18 && age <= 35;
          if (g === '36–55')    return age >= 36 && age <= 55;
          if (g === '55+')      return age >= 55;
          return false;
        });
        if (!inGroup) return null; // fails AND — exclude
        verified.push(`Age ${age}`);
      }
    }

    // AND condition 3 — income
    // monthlyIncome is stored as a formatted string e.g. "$2,000" — parse to number for comparison
    const parseIncome = (val) => {
      if (val == null) return null;
      const n = parseFloat(String(val).replace(/[$,]/g, ''));
      return isNaN(n) ? null : n;
    };
    const memberIncome = parseIncome(member.monthlyIncome);

    if (elig.maxIncome) {
      const limit = parseFloat(String(elig.maxIncome).replace(/[$,]/g, ''));
      if (memberIncome === null) {
        unverifiable.push(`Max income $${elig.maxIncome}/mo (not on profile — verify manually)`);
      } else if (memberIncome > limit) {
        return null; // fails AND — exclude
      } else {
        verified.push(`Income ${member.monthlyIncome} ≤ $${Number(limit).toLocaleString('en-US')}/mo`);
      }
    }

    if (elig.monthlyHouseholdIncome) {
      const limit = parseFloat(String(elig.monthlyHouseholdIncome).replace(/[$,]/g, ''));
      if (memberIncome === null) {
        unverifiable.push(`HH income ≤ $${elig.monthlyHouseholdIncome}/mo (not on profile — verify manually)`);
      } else if (memberIncome > limit) {
        return null; // fails AND — exclude
      } else {
        verified.push(`Income ${member.monthlyIncome} ≤ $${Number(limit).toLocaleString('en-US')}/mo (HH)`);
      }
    }

    // AND condition 4 — children (not stored on member profiles)
    if (elig.childrenCount)
      unverifiable.push(`≥ ${elig.childrenCount} dependent children (verify manually)`);
    if (elig.childAge)
      unverifiable.push(`Child age ${elig.childAge} (verify manually)`);

    return { member, verified, unverifiable };
  };

  const runMatch = async () => {
    setMatchLoading(true);
    setMatchError('');
    setMatchResults(null);
    try {
      const members = await getMembers(orgId);
      const active = members.filter(m => m.status !== 'inactive' && m.status !== 'archived');
      const elig = findMatchesScheme.eligibility || {};
      const hasCriteria = (
        elig.widowStatus?.length ||
        elig.ageGroups?.length ||
        elig.maxIncome ||
        elig.monthlyHouseholdIncome ||
        elig.childrenCount
      );

      let matched;
      if (!hasCriteria) {
        matched = active.map(m => ({ member: m, verified: [], unverifiable: [] }));
      } else {
        matched = active
          .map(m => memberMatchesScheme(m, findMatchesScheme))
          .filter(Boolean);
      }
      setMatchResults(matched);
    } catch (e) {
      setMatchError(e.message || 'Failed to load member data.');
    } finally {
      setMatchLoading(false);
    }
  };

  const exportMatchesToExcel = () => {
    if (!matchResults?.length) return;
    const rows = matchResults.map(({ member, verified, unverifiable }) => ({
      Name: member.displayName || member.name || '',
      Email: member.email || '',
      Verified: verified.join(', '),
      'Needs Manual Check': unverifiable.join('; '),
    }));
    exportRowsToExcel(rows, {
      sheetName: 'Matches',
      fileBase: `${findMatchesScheme?.name || 'scheme'}_matches`,
    });
  };

  const toggleWidowStatus = (val) => {
    setSchemeForm(prev => {
      const list = prev.eligibility.widowStatus.includes(val)
        ? prev.eligibility.widowStatus.filter(v => v !== val)
        : [...prev.eligibility.widowStatus, val];
      return { ...prev, eligibility: { ...prev.eligibility, widowStatus: list } };
    });
  };

  const toggleAgeGroup = (val) => {
    setSchemeForm(prev => {
      const list = prev.eligibility.ageGroups.includes(val)
        ? prev.eligibility.ageGroups.filter(v => v !== val)
        : [...prev.eligibility.ageGroups, val];
      return { ...prev, eligibility: { ...prev.eligibility, ageGroups: list } };
    });
  };

  const referralSchemeOptions = [...new Map(
    referrals.filter(r => r.schemeId).map(r => [r.schemeId, r.schemeName || r.schemeId])
  ).entries()];

  const filteredReferrals = referrals.filter(r => {
    const statusMatch = (() => {
      if (referralFilter === 'all') return true;
      if (referralFilter === 'pending') return r.status === 'referred';
      if (referralFilter === 'in_progress') return r.status === 'in_progress';
      if (referralFilter === 'resolved') return r.status === 'approved' || r.status === 'rejected';
      return true;
    })();
    const schemeMatch = referralSchemeFilter === 'all' || r.schemeId === referralSchemeFilter;
    return statusMatch && schemeMatch;
  });

  const fmtDate = (ts) => ts ? new Date(ts?.toDate?.() || ts).toLocaleDateString('en-SG') : '—';

  return (
    <div className="bb-editor">
      {/* Internal tab bar */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 24, paddingBottom: 0 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
              borderBottom: activeTab === t.key ? '2px solid #6B46C1' : '2px solid transparent',
              color: activeTab === t.key ? '#6B46C1' : '#6B7280',
              fontWeight: activeTab === t.key ? 600 : 400, fontSize: 14
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── GOVERNMENT PROGRAMMES ── */}
      {activeTab === 'library' && (
        <div>
          {/* Find Matches Modal */}
          {findMatchesScheme && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 840, width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Find Matches — {findMatchesScheme.name}</h3>
                <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 16 }}>
                  Match your member database against this scheme's eligibility criteria.
                </p>

                {/* Eligibility summary */}
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
                  <strong style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Eligibility Criteria</strong>
                  {[
                    findMatchesScheme.eligibility?.widowStatus?.length
                      ? `Widow Status: ${findMatchesScheme.eligibility.widowStatus.join(', ')}`
                      : null,
                    findMatchesScheme.eligibility?.maxIncome
                      ? `Max Monthly Income: $${findMatchesScheme.eligibility.maxIncome}`
                      : null,
                    findMatchesScheme.eligibility?.monthlyHouseholdIncome
                      ? `Monthly Household Income ≤ $${findMatchesScheme.eligibility.monthlyHouseholdIncome}`
                      : null,
                    findMatchesScheme.eligibility?.childAge
                      ? `Child's Age: ${findMatchesScheme.eligibility.childAge}`
                      : null,
                    findMatchesScheme.eligibility?.childrenCount
                      ? `Min Dependent Children: ${findMatchesScheme.eligibility.childrenCount}`
                      : null,
                    findMatchesScheme.eligibility?.ageGroups?.length
                      ? `Age Groups: ${findMatchesScheme.eligibility.ageGroups.join(', ')}`
                      : null,
                  ].filter(Boolean).map((line, i) => (
                    <div key={i} style={{ color: '#374151' }}>{line}</div>
                  ))}
                  {![
                    findMatchesScheme.eligibility?.widowStatus?.length,
                    findMatchesScheme.eligibility?.maxIncome,
                    findMatchesScheme.eligibility?.monthlyHouseholdIncome,
                    findMatchesScheme.eligibility?.childAge,
                    findMatchesScheme.eligibility?.childrenCount,
                    findMatchesScheme.eligibility?.ageGroups?.length,
                  ].some(Boolean) && (
                    <span style={{ color: '#9CA3AF' }}>No criteria defined — all active members will be returned.</span>
                  )}
                </div>

                {matchError && <div className="bb-alert bb-alert--error" style={{ marginBottom: 12 }}>{matchError}</div>}

                {/* Results */}
                {matchResults !== null && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>
                      {matchResults.length} member{matchResults.length !== 1 ? 's' : ''} matched
                    </div>
                    {matchResults.length === 0 ? (
                      <div style={{ fontSize: 13, color: '#6B7280' }}>No members matched the eligibility criteria.</div>
                    ) : (
                      <table className="bb-table" style={{ fontSize: 13 }}>
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Verified</th>
                            <th>Needs Manual Check</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matchResults.map(({ member, verified, unverifiable }) => (
                            <tr key={member.id}>
                              <td>{member.displayName || '—'}</td>
                              <td>{member.email || '—'}</td>
                              <td style={{ color: '#059669', fontSize: 12 }}>
                                {verified.length ? verified.join(', ') : <span style={{ color: '#9CA3AF' }}>—</span>}
                              </td>
                              <td style={{ color: '#D97706', fontSize: 12 }}>
                                {unverifiable.length ? unverifiable.join('; ') : <span style={{ color: '#9CA3AF' }}>—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}

                <div className="bb-editor-actions">
                  <button
                    className="bb-btn-primary"
                    onClick={runMatch}
                    disabled={matchLoading}
                  >
                    {matchLoading ? 'Matching…' : matchResults !== null ? 'Re-run Match' : 'Run Match'}
                  </button>
                  {matchResults !== null && (
                    <button
                      className="bb-btn-secondary"
                      onClick={exportMatchesToExcel}
                      disabled={matchLoading || matchResults.length === 0}
                    >
                      📥 Export to Excel
                    </button>
                  )}
                  <button className="bb-btn-outline-sm" onClick={() => {
                    setFindMatchesScheme(null);
                    setMatchResults(null);
                    setMatchError('');
                  }}>Close</button>
                </div>
              </div>
            </div>
          )}

          <div className="bb-editor-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0 }}>Government Programme Library</h2>
              <p className="bb-editor-desc">Manage all government programmes available for member referrals.</p>
            </div>
            <button className="bb-btn-primary" onClick={() => {
              setEditingScheme(null);
              setSchemeForm(EMPTY_SCHEME);
              setShowAddScheme(v => !v);
            }}>
              {showAddScheme && !editingScheme ? 'Cancel' : '+ Add Government Programme'}
            </button>
          </div>

          {schemesError && <div className="bb-alert bb-alert--error">{schemesError}</div>}

          {showAddScheme && (
            <form onSubmit={handleSchemeSubmit} style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 24, marginBottom: 24 }}>
              <div className="bb-section-title" style={{ marginBottom: 16 }}>{editingScheme ? 'Edit Government Programme' : 'New Government Programme'}</div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Scheme Name <span className="bb-required">*</span></label>
                  <input className="bb-input" required value={schemeForm.name} onChange={e => setSchemeForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ComCare Short-to-Medium Term Assistance" />
                </div>
                <div className="bb-form-group">
                  <label>Administrator <span className="bb-required">*</span></label>
                  <input className="bb-input" required value={schemeForm.administrator} onChange={e => setSchemeForm(p => ({ ...p, administrator: e.target.value }))} placeholder="e.g. Ministry of Social and Family Development" />
                </div>
              </div>
              <div className="bb-form-group">
                <label>Description</label>
                <textarea className="bb-textarea" rows={3} value={schemeForm.description} onChange={e => setSchemeForm(p => ({ ...p, description: e.target.value }))} placeholder="Brief description of the scheme and its purpose." />
              </div>
              <div className="bb-form-section">
                <div className="bb-section-title">Eligibility Criteria</div>
                <div className="bb-form-group">
                  <label>Widow Status</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                    {WIDOW_STATUS_OPTIONS.map(opt => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={schemeForm.eligibility.widowStatus.includes(opt)} onChange={() => toggleWidowStatus(opt)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Max Monthly Income (SGD)</label>
                    <input className="bb-input" type="number" min={0} value={schemeForm.eligibility.maxIncome} onChange={e => setSchemeForm(p => ({ ...p, eligibility: { ...p.eligibility, maxIncome: e.target.value } }))} placeholder="e.g. 1900" />
                  </div>
                  <div className="bb-form-group">
                    <label>Min Dependent Children Count</label>
                    <input className="bb-input" type="number" min={0} value={schemeForm.eligibility.childrenCount} onChange={e => setSchemeForm(p => ({ ...p, eligibility: { ...p.eligibility, childrenCount: e.target.value } }))} placeholder="e.g. 4" />
                  </div>
                </div>
                <div className="bb-form-row">
                  <div className="bb-form-group">
                    <label>Monthly Household Income ($)</label>
                    <input
                      className="bb-input"
                      type="number"
                      min={0}
                      value={schemeForm.eligibility.monthlyHouseholdIncome}
                      onChange={e => setSchemeForm(p => ({ ...p, eligibility: { ...p.eligibility, monthlyHouseholdIncome: e.target.value } }))}
                      placeholder="e.g. 2000"
                    />
                  </div>
                  <div className="bb-form-group">
                    <label>Child's Age</label>
                    <input
                      className="bb-input"
                      type="text"
                      value={schemeForm.eligibility.childAge}
                      onChange={e => setSchemeForm(p => ({ ...p, eligibility: { ...p.eligibility, childAge: e.target.value } }))}
                      placeholder="e.g. <12 or 6–17"
                    />
                  </div>
                </div>
                <div className="bb-form-group">
                  <label>Age Groups</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
                    {AGE_GROUP_OPTIONS.map(opt => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={schemeForm.eligibility.ageGroups.includes(opt)} onChange={() => toggleAgeGroup(opt)} />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="bb-form-group">
                <label>Benefits</label>
                <textarea className="bb-textarea" rows={2} value={schemeForm.benefits} onChange={e => setSchemeForm(p => ({ ...p, benefits: e.target.value }))} placeholder="Describe the financial or in-kind benefits provided." />
              </div>
              <div className="bb-form-group">
                <label>Documents Required <span className="bb-hint">(comma-separated)</span></label>
                <textarea className="bb-textarea" rows={2} value={schemeForm.documentsRequired} onChange={e => setSchemeForm(p => ({ ...p, documentsRequired: e.target.value }))} placeholder="e.g. NRIC, Death Certificate, Latest payslips" />
              </div>
              <div className="bb-form-row">
                <div className="bb-form-group">
                  <label>Application Deadline</label>
                  <input className="bb-input" type="date" value={schemeForm.deadline} onChange={e => setSchemeForm(p => ({ ...p, deadline: e.target.value }))} />
                </div>
                <div className="bb-form-group">
                  <label>Application URL</label>
                  <input className="bb-input" type="url" value={schemeForm.applicationUrl} onChange={e => setSchemeForm(p => ({ ...p, applicationUrl: e.target.value }))} placeholder="https://" />
                </div>
              </div>
              <div className="bb-editor-actions">
                <button className="bb-btn-primary" type="submit" disabled={schemeSubmitting}>
                  {schemeSubmitting ? 'Saving…' : editingScheme ? 'Save Changes' : 'Create Programme'}
                </button>
                <button className="bb-btn-outline-sm" type="button" onClick={() => { setShowAddScheme(false); setSchemeForm(EMPTY_SCHEME); setEditingScheme(null); }}>Cancel</button>
              </div>
            </form>
          )}

          {schemesLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={6}>Loading schemes…</td></tr></tbody></table>
          ) : schemes.length === 0 ? (
            <div className="bb-empty-state">No schemes added yet. Click "Add Scheme" to get started.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th>Programme Name</th>
                  <th>Administrator</th>
                  <th>Status</th>
                  <th>Eligibility Summary</th>
                  <th>Referrals</th>
                  <th>Approvals</th>
                  <th>Deadline</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schemes.map(s => {
                  const sRefs = referrals.filter(r => r.schemeId === s.id);
                  const sApproved = sRefs.filter(r => r.status === 'approved').length;
                  return (
                  <tr key={s.id}>
                    <td><strong>{s.name}</strong></td>
                    <td>{s.administrator}</td>
                    <td>
                      <span className={s.status === 'archived' ? 'bb-badge bb-badge--gray' : 'bb-badge bb-badge--green'}>
                        {s.status === 'archived' ? 'Archived' : 'Active'}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: '#6B7280', maxWidth: 220 }}>
                      {[
                        s.eligibility?.widowStatus?.join(', '),
                        s.eligibility?.maxIncome ? `Income ≤ $${s.eligibility.maxIncome}` : null,
                        s.eligibility?.monthlyHouseholdIncome ? `HH Income $${s.eligibility.monthlyHouseholdIncome}` : null,
                        s.eligibility?.childAge ? `Child Age ${s.eligibility.childAge}` : null,
                        s.eligibility?.ageGroups?.join(', ')
                      ].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{sRefs.length}</td>
                    <td style={{ textAlign: 'center' }}>{sApproved}</td>
                    <td>{fmtDate(s.deadline)}</td>
                    <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {s.status !== 'archived' && (
                        <button className="bb-btn-sm" onClick={() => handleEditScheme(s)}>Edit</button>
                      )}
                      {s.status !== 'archived' && (
                        <button className="bb-btn-danger-sm" onClick={() => handleArchive(s.id)}>Archive</button>
                      )}
                      {s.status !== 'archived' && (
                        <button className="bb-btn-sm" onClick={() => {
                          setFindMatchesScheme(s);
                          setMatchResults(null);
                          setMatchError('');
                        }}>Find Matches</button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── REFERRAL DASHBOARD ── */}
      {activeTab === 'referrals' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Applications Dashboard</h2>
            <p className="bb-editor-desc">Track and action member applications to government schemes.</p>
          </div>

          {/* Status Update Modal */}
          {statusModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 480, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <h3 style={{ marginTop: 0 }}>Update Referral Status</h3>
                <div style={{ fontSize: 14, marginBottom: 16 }}>
                  Changing status to{' '}
                  <span className={(STATUS_BADGE[statusModal.newStatus] || STATUS_BADGE.referred).cls}
                    style={(STATUS_BADGE[statusModal.newStatus] || STATUS_BADGE.referred).style}>
                    {(STATUS_BADGE[statusModal.newStatus] || STATUS_BADGE.referred).label}
                  </span>
                </div>
                <div className="bb-form-group">
                  <label>
                    Note
                    {(statusModal.newStatus === 'approved' || statusModal.newStatus === 'rejected') && (
                      <span className="bb-required"> *</span>
                    )}
                  </label>
                  <textarea
                    className="bb-textarea"
                    rows={3}
                    value={statusNote}
                    onChange={e => setStatusNote(e.target.value)}
                    placeholder={
                      statusModal.newStatus === 'approved' ? 'Describe the outcome and any next steps for the member.' :
                      statusModal.newStatus === 'rejected' ? 'Provide the reason for rejection.' :
                      'Optional — describe the current follow-up progress.'
                    }
                    autoFocus
                  />
                </div>
                <div className="bb-editor-actions">
                  <button
                    className="bb-btn-primary"
                    onClick={handleStatusChange}
                    disabled={
                      statusUpdating[statusModal.referralId] ||
                      ((statusModal.newStatus === 'approved' || statusModal.newStatus === 'rejected') && !statusNote.trim())
                    }
                  >
                    {statusUpdating[statusModal.referralId] ? 'Saving…' : 'Confirm'}
                  </button>
                  <button className="bb-btn-outline-sm" onClick={() => { setStatusModal(null); setStatusNote(''); }}>Cancel</button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, marginLeft: 24, alignItems: 'center' }}>
            <div className="bb-form-group" style={{ margin: 0, minWidth: 160 }}>
              <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 4, display: 'block' }}>Status</label>
              <select
                className="bb-select"
                value={referralFilter}
                onChange={e => setReferralFilter(e.target.value)}
                style={{ fontSize: 13, padding: '6px 10px' }}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Referred</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="bb-form-group" style={{ margin: 0, minWidth: 200 }}>
              <label style={{ fontSize: 12, color: '#6B7280', marginBottom: 4, display: 'block' }}>Scheme</label>
              <select
                className="bb-select"
                value={referralSchemeFilter}
                onChange={e => setReferralSchemeFilter(e.target.value)}
                style={{ fontSize: 13, padding: '6px 10px' }}
              >
                <option value="all">All Schemes</option>
                {referralSchemeOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
          </div>

          {referralsError && <div className="bb-alert bb-alert--error">{referralsError}</div>}

          {/* View Referral Modal */}
          {viewApp && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '32px 16px' }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 32, maxWidth: 680, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                  <h3 style={{ margin: 0 }}>Referral Details</h3>
                  <button onClick={() => setViewApp(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B7280' }}>✕</button>
                </div>
                {appsError && <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{appsError}</div>}
                <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16, marginBottom: 20, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Member</div><div style={{ fontWeight: 600 }}>{viewApp.memberName || viewApp.memberId}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Scheme</div><div style={{ fontWeight: 600 }}>{viewApp.schemeName || viewApp.schemeId}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Status</div><div>{(STATUS_BADGE[viewApp.status] || STATUS_BADGE.referred).label}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Submitted</div><div>{fmtDate(viewApp.createdAt)}</div></div>
                  <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Last Updated</div><div>{fmtDate(viewApp.updatedAt)}</div></div>
                  {viewApp.claimedByName && <div><div style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', marginBottom: 2 }}>Caseworker</div><div>{viewApp.claimedByName}</div></div>}
                </div>
                <div style={{ marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Correspondence</div>
                  <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, maxHeight: 260, overflowY: 'auto', padding: 12, marginBottom: 10, background: '#FAFAFA' }}>
                    {(viewApp.correspondence || []).length === 0 ? (
                      <div style={{ textAlign: 'center', color: '#9CA3AF', fontSize: 13, padding: '16px 0' }}>No messages yet.</div>
                    ) : (
                      [...(viewApp.correspondence || [])].sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)).map((msg, i) => (
                        <div key={i} style={{ marginBottom: 12, display: 'flex', flexDirection: msg.senderRole === 'admin' ? 'row-reverse' : 'row', gap: 8 }}>
                          <div style={{ background: msg.senderRole === 'admin' ? '#6B46C1' : '#F3F4F6', color: msg.senderRole === 'admin' ? '#fff' : '#111827', padding: '8px 12px', borderRadius: 8, maxWidth: '75%', fontSize: 13 }}>
                            <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 3, opacity: 0.85 }}>{msg.senderName} · {msg.senderRole === 'admin' ? 'Admin' : 'Member'}</div>
                            <div>{msg.text}</div>
                            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 3 }}>{msg.sentAt ? new Date(msg.sentAt).toLocaleString('en-SG', { dateStyle: 'short', timeStyle: 'short' }) : ''}</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input className="bb-input" style={{ flex: 1 }} placeholder="Send a message to the member…" value={appMessage} onChange={e => setAppMessage(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendAppMessage(); } }} />
                    <button className="bb-btn-primary" onClick={handleSendAppMessage} disabled={!appMessage.trim() || appMessageSending}>{appMessageSending ? '…' : 'Send'}</button>
                  </div>
                </div>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 10, fontSize: 14 }}>Documents</div>
                  {(viewApp.submittedDocuments || []).length === 0 ? (
                    <div style={{ color: '#9CA3AF', fontSize: 13 }}>No documents submitted by member.</div>
                  ) : (
                    (viewApp.submittedDocuments || []).map((docItem, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 6, marginBottom: 6 }}>
                        <span style={{ fontSize: 18 }}>📄</span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{docItem.name || 'Document'}</div>
                          {docItem.uploadedAt && <div style={{ fontSize: 11, color: '#9CA3AF' }}>Uploaded {fmtDate(docItem.uploadedAt)}</div>}
                        </div>
                        {docItem.url && <a href={docItem.url} target="_blank" rel="noopener noreferrer" className="bb-btn-sm" style={{ textDecoration: 'none' }}>View</a>}
                      </div>
                    ))
                  )}
                </div>
                <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="bb-btn-outline-sm" onClick={() => setViewApp(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {referralsLoading ? (
            <table className="bb-table"><tbody><tr className="bb-loading-row"><td colSpan={9}>Loading referrals…</td></tr></tbody></table>
          ) : filteredReferrals.length === 0 ? (
            <div className="bb-empty-state">No referrals found for this filter.</div>
          ) : (
            <table className="bb-table">
              <thead>
                <tr>
                  <th style={{ paddingLeft: 24 }}>Member</th>
                  <th>Scheme</th>
                  <th>Status</th>
                  <th>Days Open</th>
                  <th>Date Updated</th>
                  <th>Claimed By</th>
                  <th>Actions</th>
                  <th>Update Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredReferrals.map(r => {
                  const days = daysSince(r.createdAt) || 0;
                  const badge = STATUS_BADGE[r.status] || STATUS_BADGE.referred;
                  const isMine = r.claimedBy === currentUser.uid;
                  const isOpen = r.status !== 'approved' && r.status !== 'rejected';
                  return (
                    <tr key={r.id}>
                      <td style={{ paddingLeft: 24 }}>{r.memberName || r.memberId}</td>
                      <td>{r.schemeName || r.schemeId}</td>
                      <td><span className={badge.cls} style={badge.style}>{badge.label}</span></td>
                      <td><SlaCell days={days} /></td>
                      <td>{fmtDate(r.updatedAt)}</td>
                      <td>{r.claimedByName || (r.claimedBy ? r.claimedBy : <span style={{ color: '#9CA3AF' }}>—</span>)}</td>
                      <td>
                        {isOpen && !r.claimedBy && (
                          <button className="bb-btn-sm" onClick={() => handleClaim(r.id)} disabled={!!statusUpdating[r.id]}>Claim</button>
                        )}
                        {isOpen && isMine && (
                          <button className="bb-btn-outline-sm" onClick={() => handleRelease(r.id)}>Release</button>
                        )}
                      </td>
                      <td>
                        {isOpen && isMine && (
                          <select
                            className="bb-select"
                            style={{ fontSize: 12, padding: '4px 8px' }}
                            value={r.status}
                            disabled={statusUpdating[r.id]}
                            onChange={e => {
                              if (e.target.value !== r.status) {
                                setStatusModal({ referralId: r.id, newStatus: e.target.value });
                                setStatusNote('');
                              }
                            }}
                          >
                            <option value="referred">Referred</option>
                            <option value="in_progress">In Progress</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                          </select>
                        )}
                      </td>
                      <td>
                        <button className="bb-btn-sm" onClick={() => handleViewApp(r)}>View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── BULK MATCH ── */}
      {activeTab === 'bulkMatch' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Bulk Match</h2>
            <p className="bb-editor-desc">When you add a new scheme, flag members who may qualify.</p>
          </div>
          <div className="bb-alert bb-alert--info" style={{ marginBottom: 16 }}>
            Connect your member data to run automatic eligibility matching.
          </div>
          <div className="bb-form-group" style={{ marginLeft: 24 }}>
            <label>Eligibility Criteria</label>
            <textarea
              className="bb-textarea"
              rows={6}
              placeholder="Paste or describe the eligibility criteria to match against your member database (e.g. widowed, income < $1900/month, children under 16)."
            />
          </div>
          <div className="bb-editor-actions">
            <button className="bb-btn-primary" disabled>Run Match (Connect Member Data)</button>
          </div>
        </div>
      )}

      {/* ── REPORTING ── */}
      {activeTab === 'reporting' && (
        <div>
          <div className="bb-editor-header">
            <h2 style={{ margin: 0 }}>Reporting</h2>
            <p className="bb-editor-desc">Overview of scheme activity and referral outcomes.</p>
          </div>
          <div className="bb-stats-row" style={{ display: 'flex', gap: 16, marginBottom: 32 }}>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{stats.activeSchemes}</div>
              <div className="bb-stat-label">Active Schemes</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{stats.openReferrals}</div>
              <div className="bb-stat-label">Open Referrals</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{stats.approvalsThisMonth}</div>
              <div className="bb-stat-label">Approvals This Month</div>
            </div>
            <div className="bb-stat-card">
              <div className="bb-stat-value">{stats.avgDays}d</div>
              <div className="bb-stat-label">Avg Days to Resolve</div>
            </div>
          </div>
          <div className="bb-section-title" style={{ marginBottom: 12 }}>Most Used Schemes</div>
          {schemes.length === 0 ? (
            <div className="bb-empty-state">No scheme data available.</div>
          ) : (
            <table className="bb-table">
              <thead><tr><th>Scheme</th><th>Administrator</th><th>Total Referrals</th><th>Approvals</th></tr></thead>
              <tbody>
                {schemes.filter(s => s.status !== 'archived').map(s => {
                  const sRefs = referrals.filter(r => r.schemeId === s.id);
                  const sApproved = sRefs.filter(r => r.status === 'approved').length;
                  return (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>{s.administrator}</td>
                      <td>{sRefs.length}</td>
                      <td>{sApproved}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
